import { PhoneIcon, TrainIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { UnreadNotificationIcon } from '@rsos/components/capstone/UnreadNotificationIcon/UnreadNotificationIcon';
import type { NorfolkSouthernRequestResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { formatDateWithLocalTimezone } from '../../../../../utils/dateUtilities';
import { formatTrainLabel } from '../../../../../utils/norfolkSouthern/trains';
import {
  OriginSourcePill,
  OriginSourceTitleRow,
} from '../../../../AirAmbulanceForm/etl/OriginSource.styles';
import styles from '../ResourceRequestPanel/TransportListItem.module.css';
import {
  StyledTransportListItem,
  TransportIconWrapper,
  TransportDetails,
  DetailRow,
} from '../ResourceRequestPanel/TransportListItem.styles';
import {
  formatRequesterName,
  resolveNorfolkSouthernStatusChip,
} from './norfolkSouthernStatusUtils';

interface NorfolkSouthernListItemProps {
  request: NorfolkSouthernRequestResponse;
  isUnread: boolean;
  onClick: () => void;
}

function NorfolkSouthernListItem({
  request,
  isUnread,
  onClick,
}: NorfolkSouthernListItemProps) {
  const { train, requester, reason } = request.request;
  const statusChip = resolveNorfolkSouthernStatusChip(request);
  const createdAtDisplay = formatDateWithLocalTimezone(
    request.createdAt,
    'HH:mm',
  );
  const chipToneClass =
    statusChip.tone === 'error'
      ? styles.statusBadgeError
      : styles.statusBadgeSuccess;

  return (
    <StyledTransportListItem
      onClick={onClick}
      data-name="norfolk-southern-list-item"
    >
      <TransportIconWrapper>
        <TrainIcon color="primaryText" height={20} width={20} />
      </TransportIconWrapper>

      <TransportDetails>
        <OriginSourceTitleRow>
          <Text
            color="primaryText"
            size="large"
            fontWeight="bold"
            lineHeight="1.2"
          >
            Train Consist
          </Text>
          {createdAtDisplay && (
            <span className={styles.createdAtContainer}>
              <span
                className={styles.timestamp}
                data-testid="norfolk-southern-created-at"
              >
                {createdAtDisplay}
              </span>
              {isUnread && (
                <UnreadNotificationIcon
                  data-testid="norfolk-southern-list-item-unread-icon"
                  position="center"
                />
              )}
            </span>
          )}
        </OriginSourceTitleRow>

        <div className={styles.statusRow}>
          <span
            data-testid="norfolk-southern-status-chip"
            className={`${styles.statusBadge} ${chipToneClass}`}
          >
            {statusChip.display}
          </span>
          <OriginSourcePill
            data-testid="norfolk-southern-origin"
            className={styles.statusRowSource}
          >
            Unite
          </OriginSourcePill>
        </div>

        <DetailRow>
          <Text color="primaryText" size="small" fontWeight="bold">
            {formatTrainLabel(train)}
          </Text>
          <Text color="secondaryText" size="small">
            · Norfolk Southern · {formatRequesterName(requester)}
          </Text>
        </DetailRow>

        <DetailRow>
          <Text color="secondaryText" size="small" ellipsis>
            {reason}
          </Text>
        </DetailRow>

        <DetailRow>
          <PhoneIcon color="secondaryText" height={12} width={12} />
          <Text color="secondaryText" size="small">
            {request.callerId
              ? formatInternationalPhoneNumberHuman(request.callerId)
              : '—'}
          </Text>
        </DetailRow>
      </TransportDetails>
    </StyledTransportListItem>
  );
}

export default NorfolkSouthernListItem;
